import { c as _c } from "react/compiler-runtime";
import CollectionsMenuIcon from "@/components/common/icons/CollectionsMenuIcon";
import type { SidebarSection } from "@/components/navigation/navTypes";
import { shouldHideSubscriptions } from "@/components/user/layout/userPageVisibility";
import { AboutSection } from "@/types/enums";
import { DocumentTextIcon, UsersIcon, WrenchIcon } from "@heroicons/react/24/outline";
import { useMemo, type ComponentType } from "react";
function getNetworkSection(): SidebarSection {
  return {
    key: "network",
    name: "Network",
    icon: UsersIcon,
    items: [{
      name: "Identities",
      href: "/network"
    }, {
      name: "Activity",
      href: "/network/activity"
    }, {
      name: "Groups",
      href: "/network/groups"
    }, {
      name: "NFT Activity",
      href: "/nft-activity"
    }, {
      name: "Memes Calendar",
      href: "/meme-calendar"
    }, {
      name: "TDH",
      href: "/network/tdh"
    }, {
      name: "xTDH",
      href: "/xtdh"
    }],
    subsections: [{
      name: "Metrics",
      items: [{
        name: "Health",
        href: "/network/health"
      }, {
        name: "Definitions",
        href: "/network/definitions"
      }, {
        name: "Levels",
        href: "/network/levels"
      }, {
        name: "Network Stats",
        href: "/network/health/network-tdh"
      }]
    }]
  };
}
function getCollectionsSection(): SidebarSection {
  return {
    key: "collections",
    name: "Collections",
    icon: CollectionsMenuIcon,
    items: [{
      name: "The Memes",
      href: "/the-memes"
    }, {
      name: "6529 Gradient",
      href: "/6529-gradient"
    }, {
      name: "NextGen",
      href: "/nextgen"
    }, {
      name: "Meme Lab",
      href: "/meme-lab"
    }, {
      name: "ReMemes",
      href: "/rememes"
    }],
    subsections: []
  };
}
function getToolsSection(appWalletsSupported: boolean, hideSubscriptions: boolean): SidebarSection {
  return {
    key: "tools",
    name: "Tools",
    icon: WrenchIcon,
    items: [],
    subsections: [{
      name: "NFT Delegation",
      items: [{
        name: "Delegation Center",
        href: "/delegation/delegation-center"
      }, {
        name: "Wallet Architecture",
        href: "/delegation/wallet-architecture"
      }, {
        name: "Delegation FAQs",
        href: "/delegation/delegation-faq"
      }, {
        name: "Consolidation Use Cases",
        href: "/delegation/consolidation-use-cases"
      }, {
        name: "Wallet Checker",
        href: "/delegation/wallet-checker"
      }]
    }, {
      name: "The Memes Tools",
      items: [...(hideSubscriptions ? [] : [{
        name: "Subscriptions Report",
        href: "/tools/subscriptions-report"
      }]), {
        name: "Memes Accounting",
        href: "/meme-accounting"
      }, {
        name: "Memes Gas",
        href: "/meme-gas"
      }]
    }, {
      name: "Other Tools",
      items: [...(appWalletsSupported ? [{
        name: "App Wallets",
        href: "/tools/app-wallets"
      }] : []), {
        name: "API",
        href: "/tools/api"
      }, {
        name: "EMMA",
        href: "/emma"
      }, {
        name: "Block Finder",
        href: "/tools/block-finder"
      }]
    }, {
      name: "Open Data",
      items: [{
        name: "Open Data",
        href: "/open-data"
      }, {
        name: "Network Metrics",
        href: "/open-data/network-metrics"
      }, ...(hideSubscriptions ? [] : [{
        name: "Meme Subscriptions",
        href: "/open-data/meme-subscriptions"
      }]), {
        name: "Rememes",
        href: "/open-data/rememes"
      }, {
        name: "Team",
        href: "/open-data/team"
      }, {
        name: "Royalties",
        href: "/open-data/royalties"
      }]
    }]
  };
}
function getAboutSection(hideSubscriptions: boolean): SidebarSection {
  return {
    key: "about",
    name: "About",
    icon: DocumentTextIcon,
    items: [{
      name: "GDRC1",
      href: `/about/${AboutSection.GDRC1}`
    }],
    subsections: [{
      name: "Support",
      items: [{
        name: "FAQ",
        href: `/about/${AboutSection.FAQ}`
      }, {
        name: "Apply",
        href: `/about/${AboutSection.APPLY}`
      }, {
        name: "Contact Us",
        href: `/about/${AboutSection.CONTACT_US}`
      }]
    }, {
      name: "NFTs",
      items: [{
        name: "The Memes",
        href: `/about/${AboutSection.MEMES}`
      }, ...(hideSubscriptions ? [] : [{
        name: "Subscriptions",
        href: `/about/${AboutSection.SUBSCRIPTIONS}`
      }]), {
        name: "Minting",
        href: `/about/${AboutSection.MINTING}`
      }, {
        name: "Nakamoto Threshold",
        href: `/about/${AboutSection.NAKAMOTO_THRESHOLD}`
      }, {
        name: "Meme Lab",
        href: `/about/${AboutSection.MEME_LAB}`
      }, {
        name: "Gradients",
        href: `/about/${AboutSection.GRADIENTS}`
      }]
    }, {
      name: "NFT Delegation",
      items: [{
        name: "About NFTD",
        href: `/about/${AboutSection.NFT_DELEGATION}`
      }, {
        name: "Primary Address",
        href: `/about/${AboutSection.PRIMARY_ADDRESS}`
      }]
    }, {
      name: "6529 Capital",
      items: [{
        name: "About 6529 Capital",
        href: `/capital`
      }, {
        name: "Company Portfolio",
        href: `/capital/company-portfolio`
      }, {
        name: "NFT Fund",
        href: `/capital/fund`
      }]
    }, {
      name: "Resources",
      items: [{
        name: "Data Decentralization",
        href: `/about/${AboutSection.DATA_DECENTR}`
      }, {
        name: "ENS",
        href: `/about/${AboutSection.ENS}`
      }, {
        name: "License",
        href: `/about/${AboutSection.LICENSE}`
      }, {
        name: "Release Notes",
        href: `/about/${AboutSection.RELEASE_NOTES}`
      }]
    }]
  };
}
function buildSidebarSections(appWalletsSupported: boolean, hideSubscriptions: boolean): SidebarSection[] {
  return [getNetworkSection(), getCollectionsSection(), getToolsSection(appWalletsSupported, hideSubscriptions), getAboutSection(hideSubscriptions)];
}
export function useSidebarSections(appWalletsSupported, isIos, country) {
  const $ = _c(6);
  let t0;
  if ($[0] !== country || $[1] !== isIos) {
    t0 = shouldHideSubscriptions({
      capacitorIsIos: isIos,
      country
    });
    $[0] = country;
    $[1] = isIos;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hideSubscriptions = t0;
  let t1;
  if ($[3] !== appWalletsSupported || $[4] !== hideSubscriptions) {
    t1 = buildSidebarSections(appWalletsSupported, hideSubscriptions);
    $[3] = appWalletsSupported;
    $[4] = hideSubscriptions;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function useSectionMap(sections) {
  const $ = _c(2);
  let t0;
  if ($[0] !== sections) {
    t0 = new Map(sections.map(_temp));
    $[0] = sections;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp(section) {
  return [section.key, section];
}
export interface SidebarPageEntry {
  name: string;
  href: string;
  section: string;
  subsection?: string | undefined;
  icon?: ComponentType<{
    className?: string | undefined;
  }> | undefined;
}
export function mapSidebarSectionsToPages(sections: SidebarSection[]): SidebarPageEntry[] {
  return sections.flatMap(section => {
    const sectionIcon = section.icon;
    const sectionItems: SidebarPageEntry[] = section.items.map(item => ({
      name: item.name,
      href: item.href,
      section: section.name,
      icon: sectionIcon
    }));
    const subsectionItems = section.subsections?.flatMap(subsection => subsection.items.map(item => ({
      name: item.name,
      href: item.href,
      section: section.name,
      subsection: subsection.name,
      icon: sectionIcon
    }))) ?? [];
    return [...sectionItems, ...subsectionItems];
  });
}